// src/hooks/useSectorEmissionsTS.js
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { useMemo } from "react";

const SECTOR_TS_QUERY = gql`
  query SectorEmissionsTS($scenario: String) {
    emis: timeSeries(view: EMIS_GHG_BY_SET, scenario: $scenario) {
      scenario
      year
      group
      value
    }
    food: timeSeries(view: FOOD_GHG_TOTAL, scenario: $scenario) {
      scenario
      year
      group
      value
    }
  }
`;


function canonicalSector(label) {
  const s = (label || "").toLowerCase().trim();
  if (s.startsWith("food")) return "food";
  if (s.startsWith("build")) return "buildings";
  if (s.startsWith("trans")) return "transport";
  if (s.startsWith("ind")) return "industry";
  return null; // ignore Electricity/Other for this chart
}

export function useSectorEmissionsTS({ scenario, from, to } = {}) {
  const { data, loading, error } = useQuery(SECTOR_TS_QUERY, {
    variables: { scenario },
    fetchPolicy: "cache-first",
  });

  // Collect & sort years (optionally filter)
  const labels = useMemo(() => {
    const years = new Set();
    for (const r of data?.timeSeries ?? []) {
      if (typeof r.year !== "number") continue;
      if (from && r.year < from) continue;
      if (to && r.year > to) continue;
      years.add(r.year);
    }
    return Array.from(years).sort((a, b) => a - b);
  }, [data, from, to]);

  // Build sector -> year -> value map
  const bySectorYear = useMemo(() => {
    const map = { food: {}, buildings: {}, transport: {}, industry: {} };
    for (const r of data?.timeSeries ?? []) {
      if (from && r.year < from) continue;
      if (to && r.year > to) continue;
      const key = canonicalSector(r.group);
      if (!key) continue;
      const v = typeof r.value === "number" ? r.value : null;
      if (v != null) map[key][r.year] = v; // latest wins if duplicates
    }
    return map;
  }, [data, from, to]);



  // Align arrays to labels for each sector
  const seriesBySector = useMemo(() => {
    const toSeries = (key) => labels.map((y) => bySectorYear[key]?.[y] ?? null);
    return {
      food: toSeries("food"),
      buildings: toSeries("buildings"),
      transport: toSeries("transport"),
      industry: toSeries("industry"),
    };
  }, [labels, bySectorYear]);

  return { labels, seriesBySector, loading, error };
}

